/**
 * Retailer + StoreLocation.
 *
 * Ported from legacy `frontend/src/types.ts`. Retailer (chain/brand) and
 * StoreLocation (one physical or online place owned by a Retailer) coexist.
 *
 * The schema follows backend/src/db/schema.ts after T12.0.1.7.1 cleanup —
 * dropped fields (chain_name, store_type, country, price_tier,
 * accepts_coupons, phone, email, website, hours, departments, features) are
 * intentionally absent. The new `is_online` boolean and structured
 * `opening_hours` jsonb are present; physical-store fields are optional/null
 * when the store is online.
 */

import { z } from 'zod';
import { pgNumeric } from './primitives';

export const retailerSchema = z.object({
  id: z.string(),
  name: z.string(),
  normalized_name: z.string(),
  country: z.string().optional(),
  website_url: z.string().optional(),
  logo_url: z.string().optional(),
});
export type Retailer = z.infer<typeof retailerSchema>;

// Per `OpeningHours` in backend/src/db/schema.ts — each weekday is either a
// 24-hour `HH:MM` open/close window or `{ closed: true }`. `null` overall for
// online stores.
const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
export const openingHoursDaySchema = z.union([
  z.object({
    open: z.string().regex(HHMM_REGEX),
    close: z.string().regex(HHMM_REGEX),
  }),
  z.object({ closed: z.literal(true) }),
]);
export const openingHoursSchema = z.object({
  mon: openingHoursDaySchema,
  tue: openingHoursDaySchema,
  wed: openingHoursDaySchema,
  thu: openingHoursDaySchema,
  fri: openingHoursDaySchema,
  sat: openingHoursDaySchema,
  sun: openingHoursDaySchema,
});
export type OpeningHoursDay = z.infer<typeof openingHoursDaySchema>;
export type OpeningHours = z.infer<typeof openingHoursSchema>;

/**
 * StoreLocation — household-scoped store row. Physical fields (`address`,
 * `latitude`, `longitude`, `place_id`, `opening_hours`, `distance_km`) are
 * all optional/null when `is_online = true`.
 */
export const storeLocationSchema = z.object({
  id: z.string(),
  household_id: z.string(),
  name: z.string(),
  address: z.string().nullable().optional(),
  retailer_id: z.string().nullable().optional(),
  is_favorite: z.boolean(),
  is_system: z.boolean(),
  is_online: z.boolean(),
  display_order: z.number().nullable().optional(),
  place_id: z.string().nullable().optional(),
  latitude: pgNumeric.nullable().optional(),
  longitude: pgNumeric.nullable().optional(),
  distance_km: pgNumeric.nullable().optional(),
  opening_hours: openingHoursSchema.nullable().optional(),
  // T28.7 — the shared store row's lifecycle status. A store an admin
  // deactivated stays in a household's selection but reads as *closed*; the
  // client can surface that from this field. Optional so older payloads (and
  // any read that doesn't carry it) still validate; defaults to active-meaning.
  status: z.string().optional(),
});
export type StoreLocation = z.infer<typeof storeLocationSchema>;

// ---------------------------------------------------------------------------
// /api/stores response shapes
// ---------------------------------------------------------------------------

export const storesResponseSchema = z.object({
  stores: z.array(storeLocationSchema),
});
export type StoresResponse = z.infer<typeof storesResponseSchema>;

// TD39 — the store CREATE surface is gone. `storelocations` is platform-authored
// (one `auto` writer + admin `manual` edits); households can only SELECT existing
// catalog rows (resolve-only via the import/selection path). "Add an online store"
// now folds into that selection flow (pick a curated online-store row, submitted
// id-only). The old `createStoreRequestSchema` / `createStoreResponseSchema` +
// `POST /api/storelocations` are removed with the write path.

export const toggleFavoriteResponseSchema = z.object({
  success: z.boolean(),
  isFavorite: z.boolean(),
});
export type ToggleFavoriteResponse = z.infer<typeof toggleFavoriteResponseSchema>;
